import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Phone, Star, Quote, Plus, Minus, MapPin, Mail, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, KineticLines, WordsReveal, Counter, Parallax, Eyebrow, GoldButton, OutlineButton, EASE } from "@/lib/ui";
import { getSite } from "@/data/site";
import { getServices } from "@/data/services";
import { useLang } from "@/i18n/lang";

const useS = () => { const { lang } = useLang(); return { ...getSite(lang), lang }; };

const Hero = () => {
  const { ui, CONTACT, book, call } = useS();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const gx = useSpring(mx, { stiffness: 60, damping: 20 }), gy = useSpring(my, { stiffness: 60, damping: 20 });
  const onMove = (e) => { const r = e.currentTarget.getBoundingClientRect(); mx.set(e.clientX - r.left - r.width / 2); my.set(e.clientY - r.top - r.height / 2); };
  return (
    <section id="home" ref={ref} onMouseMove={onMove} className="relative h-screen min-h-[720px] overflow-hidden bg-ink grain" data-testid="hero">
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0">
        <img src="/images/instr.jpg" alt="Cabinet stomatologic modern Glamour Dent" className="w-full h-full object-cover object-center" loading="eager" />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/50" />
      </motion.div>
      <motion.div style={{ x: gx, y: gy }} className="pointer-events-none absolute left-1/2 top-1/2 w-[420px] h-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/15 blur-[110px]" />
      <div className="absolute top-[22%] right-[12%] w-24 h-24 rounded-full border border-gold/30 animate-float hidden md:block" />
      <div className="absolute bottom-[26%] right-[24%] w-3 h-3 rounded-full bg-gold animate-float hidden md:block" style={{ animationDelay: "1.5s" }} />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }}><Eyebrow dark>{ui.heroEyebrow}</Eyebrow></motion.div>
        <h1 className="font-serif font-light text-white leading-[0.95] text-5xl sm:text-7xl lg:text-[5.5rem] tracking-tighter max-w-4xl">
          <KineticLines lines={[ui.heroL1, <>{ui.heroL2a}<span className="gold-text italic">{ui.heroL2gold}</span></>]} delay={0.5} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9 }} className="mt-8 max-w-xl text-white/65 text-lg font-light leading-relaxed">{ui.heroSub}</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
          <GoldButton data-testid="hero-book-btn" onClick={() => { const el = document.getElementById("contact"); if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 }); else if (el) el.scrollIntoView({ behavior: "smooth" }); }}>{book} <ArrowUpRight className="w-4 h-4" /></GoldButton>
          <a href={CONTACT.phoneHref} data-testid="hero-call-btn"><OutlineButton dark><Phone className="w-4 h-4" /> {call}</OutlineButton></a>
        </motion.div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="font-accent text-[10px] tracking-[0.3em] uppercase">{ui.scroll}</span>
        <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </div>
    </section>
  );
};

const StatsBar = () => { const { STATS } = useS(); return (
  <section className="bg-charcoal py-16 border-y border-white/5" data-testid="stats">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1} className="text-center lg:text-left">
          <div className="font-accent text-4xl md:text-5xl text-white font-light"><Counter to={s.value} suffix={s.suffix} /></div>
          <div className="mt-2 h-px w-8 bg-gold mx-auto lg:mx-0" />
          <p className="mt-3 text-white/50 text-sm font-accent tracking-wide">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
); };

const About = () => { const { ui } = useS(); return (
  <section id="about" className="bg-white text-ink py-28 md:py-36" data-testid="about">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
      <div className="relative">
        <Parallax offset={40}><div className="overflow-hidden rounded-3xl"><img src="https://customer-assets-wrfwihn1.emergentagent.net/job_sophisticate-dental/artifacts/05jmvp0h_469552004_122157537818299319_5294606908535372613_n.jpg" alt="Cabinet Glamour Dent — recepție modernă" className="w-full h-[560px] object-cover" /></div></Parallax>
        <div className="absolute -bottom-8 right-4 md:right-8 glass-light rounded-2xl px-8 py-6 shadow-xl">
          <div className="font-accent text-3xl text-ink"><Counter to={100} suffix="%" /></div>
          <p className="text-ink/60 text-xs tracking-wide font-accent uppercase mt-1">{ui.yearsLabel}</p>
        </div>
      </div>
      <div>
        <Eyebrow>{ui.aboutEye}</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight"><WordsReveal text={ui.aboutTitle} /></h2>
        <Reveal delay={0.15}>
          <p className="mt-8 text-ink/60 text-lg font-light leading-relaxed">{ui.aboutP1}</p>
          <p className="mt-5 text-ink/60 text-lg font-light leading-relaxed">{ui.aboutP2}</p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10 flex gap-10">
          {ui.aboutMini.map(([l, v]) => (<div key={l}><div className="font-accent text-2xl text-gold">{v}</div><p className="text-ink/50 text-xs tracking-wide font-accent uppercase mt-1">{l}</p></div>))}
        </Reveal>
      </div>
    </div>
  </section>
); };

const Philosophy = () => { const { ui, PHILOSOPHY } = useS(); return (
  <section className="bg-ink text-white py-28 md:py-36 grain relative" data-testid="philosophy">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl"><Eyebrow dark>{ui.philoEye}</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.philoPre}<span className="gold-text italic">{ui.philoGold}</span>{ui.philoPost}</h2></div>
      <div className="mt-20 grid md:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden">
        {PHILOSOPHY.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.12} className="bg-ink p-10 md:p-12 group hover:bg-charcoal transition-colors duration-500">
            <span className="font-serif text-6xl text-white/10 group-hover:text-gold/40 transition-colors duration-500">{p.no}</span>
            <h3 className="font-serif text-2xl mt-6 mb-4">{p.title}</h3>
            <p className="text-white/55 font-light leading-relaxed">{p.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
); };

const Marquee = () => { const { ui } = useS(); const words = ui.marquee; return (
  <div className="bg-gold py-6 overflow-hidden" data-testid="marquee">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...words, ...words, ...words, ...words].map((w, i) => (<span key={i} className="mx-8 font-serif italic text-2xl md:text-3xl text-ink/90 flex items-center gap-8">{w} <Star className="w-4 h-4 fill-ink/70 text-ink/70" /></span>))}
    </div>
  </div>
); };

const Why = () => { const { ui, WHY } = useS(); return (
  <section className="bg-white text-ink py-28 md:py-36" data-testid="why">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl mb-16"><Eyebrow>{ui.whyEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.whyPre}<span className="gold-text italic">{ui.whyGold}</span></h2></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY.map((w, i) => { const Icon = Icons[w.icon] || Icons.Sparkles; return (
          <Reveal key={w.title} delay={(i % 3) * 0.1}>
            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.4, ease: EASE }} className="h-full rounded-3xl border border-ink/8 p-8 bg-white hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.2)] transition-shadow duration-500">
              <div className="w-12 h-12 rounded-full bg-ink flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></div>
              <h3 className="font-serif text-2xl mt-6 mb-3">{w.title}</h3>
              <p className="text-ink/55 font-light leading-relaxed">{w.text}</p>
            </motion.div>
          </Reveal>); })}
      </div>
    </div>
  </section>
); };

const ServicesPreview = () => { const { ui, lang } = useS(); const featured = getServices(lang).slice(0, 5); return (
  <section className="bg-charcoal text-white py-28 md:py-36 grain relative" data-testid="services-preview">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div className="max-w-2xl"><Eyebrow dark>{ui.servEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.servPre}<span className="gold-text italic">{ui.servGold}</span></h2></div>
        <Link to="/services" data-testid="view-all-services"><OutlineButton dark>{ui.viewAll} <ArrowUpRight className="w-4 h-4" /></OutlineButton></Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[240px]">
        {featured.map((s, i) => { const Icon = Icons[s.icon] || Icons.Sparkles; const big = i === 0; return (
          <Reveal key={s.slug} delay={(i % 3) * 0.08} className={big ? "md:col-span-2 md:row-span-2" : ""}>
            <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block relative h-full w-full rounded-3xl overflow-hidden">
              <img src={s.hero} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-between p-7">
                <div className="w-11 h-11 rounded-full glass-dark flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></div>
                <div><h3 className={`font-serif ${big ? "text-4xl" : "text-2xl"} mb-1`}>{s.name}</h3>
                  <p className="text-white/60 text-sm font-light flex items-center gap-2 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">{s.tagline} <ArrowUpRight className="w-4 h-4 text-gold" /></p></div>
              </div>
            </Link>
          </Reveal>); })}
      </div>
    </div>
  </section>
); };

const Doctors = () => { const { ui, TEAM, reel } = useS(); return (
  <section id="doctors" className="bg-white text-ink py-28 md:py-36" data-testid="doctors">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl mb-16"><Eyebrow>{ui.docEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.docPre}<span className="gold-text italic">{ui.docGold}</span></h2></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {TEAM.map((d, i) => (
          <Reveal key={d.name} delay={(i % 3) * 0.1}><div className="group" data-testid={`doctor-card-${i}`}>
            <div className="relative overflow-hidden rounded-3xl">
              <img src={d.img} alt={d.name} className="w-full h-[440px] object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="absolute bottom-5 left-5 right-5 text-white text-sm font-light opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">{d.bio}</p>
            </div>
            <h3 className="font-serif text-2xl mt-5">{d.name}</h3>
            <p className="text-gold font-accent text-xs tracking-[0.15em] uppercase mt-1">{d.role}</p>
          </div></Reveal>
        ))}
      </div>

      <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center bg-ink text-white rounded-3xl p-8 md:p-12 grain relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4"><span className="h-px w-8 bg-gold" /><span className="font-accent text-[11px] tracking-[0.24em] uppercase text-white/60">{ui.docWatch}</span></div>
          <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight">{ui.docReelCaption}</h3>
        </div>
        <div className="relative z-10 justify-self-center lg:justify-self-end w-full" style={{ maxWidth: 340 }}>
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-ink">
            <iframe title="Glamour Dent Reel" data-testid="doctor-reel" src={reel} width="340" height="604" style={{ border: "none", overflow: "hidden", width: "100%", display: "block" }} scrolling="no" frameBorder="0" allowFullScreen allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
          </div>
        </div>
      </div>
    </div>
  </section>
); };

const Technology = () => { const { ui } = useS(); return (
  <section className="bg-ink text-white py-28 md:py-36 grain relative" data-testid="technology">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <Eyebrow dark>{ui.techEye}</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight">{ui.techPre}<span className="gold-text italic">{ui.techGold}</span></h2>
        <Reveal delay={0.15}><p className="mt-8 text-white/55 text-lg font-light leading-relaxed">{ui.techP}</p></Reveal>
        <div className="mt-10 space-y-5">
          {ui.techItems.map(([t, d], i) => (
            <Reveal key={t} delay={0.2 + i * 0.1} className="flex gap-4 items-start border-b border-white/10 pb-5">
              <span className="text-gold font-accent text-sm mt-1">0{i + 1}</span>
              <div><h4 className="font-medium text-lg">{t}</h4><p className="text-white/45 font-light text-sm mt-1">{d}</p></div>
            </Reveal>
          ))}
        </div>
      </div>
      <Parallax offset={50}><div className="overflow-hidden rounded-3xl"><img src="https://customer-assets-wrfwihn1.emergentagent.net/job_sophisticate-dental/artifacts/e5esgm6i_469416885_122157537878299319_8548049487488955938_n.jpg" alt="Echipamente dentare moderne Glamour Dent" className="w-full h-[600px] object-cover" /></div></Parallax>
    </div>
  </section>
); };

const Journey = () => { const { ui, JOURNEY } = useS(); return (
  <section className="bg-white text-ink py-28 md:py-36" data-testid="journey">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl mb-16"><Eyebrow>{ui.journeyEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.journeyPre}<span className="gold-text italic">{ui.journeyGold}</span></h2></div>
      <div className="grid md:grid-cols-4 gap-8 relative">
        <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-ink/10" />
        {JOURNEY.map((j, i) => (
          <Reveal key={j.no} delay={i * 0.12} className="relative">
            <div className="w-12 h-12 rounded-full bg-ink text-gold font-accent flex items-center justify-center relative z-10">{j.no}</div>
            <h3 className="font-serif text-2xl mt-6 mb-3">{j.title}</h3><p className="text-ink/55 font-light leading-relaxed">{j.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
); };

const Gallery = () => { const { ui } = useS(); const shots = [
  "/ortho/ortho2.jpg",
  "/ortho/ortho3.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_sophisticate-dental/artifacts/m98ud9hy_469325596_122157537890299319_1224778722731794557_n.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_sophisticate-dental/artifacts/e5esgm6i_469416885_122157537878299319_8548049487488955938_n.jpg",
  "/ortho/ortho4.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_sophisticate-dental/artifacts/05jmvp0h_469552004_122157537818299319_5294606908535372613_n.jpg"]; return (
  <section id="gallery" className="bg-charcoal text-white py-28 md:py-36 grain relative" data-testid="gallery">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl mb-16"><Eyebrow dark>{ui.galEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.galPre}<span className="gold-text italic">{ui.galGold}</span></h2></div>
      <div className="columns-2 md:columns-3 gap-6 space-y-6">
        {shots.map((src, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08} className="break-inside-avoid">
            <div className="group overflow-hidden rounded-3xl relative"><img src={src} alt={`Smile ${i + 1}`} className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${i % 2 === 0 ? "h-80" : "h-64"}`} /><div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl" /></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
); };

const Testimonials = () => {
  const { TESTIMONIALS } = useS();
  const [[active, dir], setState] = useState([0, 1]);
  const paused = useRef(false);
  const n = TESTIMONIALS.length;
  const go = (i) => setState(([a]) => [(i + n) % n, i >= a ? 1 : -1]);
  useEffect(() => {
    const id = setInterval(() => { if (!paused.current) setState(([a]) => [(a + 1) % n, 1]); }, 6500);
    return () => clearInterval(id);
  }, [n]);
  const t = TESTIMONIALS[active];
  return (
    <section id="reviews" onMouseEnter={() => (paused.current = true)} onMouseLeave={() => (paused.current = false)} className="bg-white text-ink py-28 md:py-36 overflow-hidden" data-testid="testimonials">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <div className="flex justify-center mb-8"><Quote className="w-12 h-12 text-gold" /></div>
        <div className="relative min-h-[280px] md:min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={active} custom={dir}
              initial={(d) => ({ opacity: 0, x: d > 0 ? 70 : -70 })}
              animate={{ opacity: 1, x: 0 }}
              exit={(d) => ({ opacity: 0, x: d > 0 ? -70 : 70 })}
              transition={{ duration: 0.6, ease: EASE }} className="w-full">
              <blockquote data-testid="testimonial-quote" className="font-serif italic font-light text-2xl md:text-4xl leading-snug tracking-tight">“{t.quote}”</blockquote>
              <div className="flex justify-center gap-1 mt-8">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}</div>
              <p className="font-accent tracking-wide mt-4 text-ink">{t.name}</p><p className="text-ink/45 text-sm">{t.role}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-3 mt-12">{TESTIMONIALS.map((_, i) => (<button key={i} data-testid={`testimonial-dot-${i}`} aria-label={`Review ${i + 1}`} onClick={() => go(i)} className={`h-2 rounded-full transition-all duration-400 ${i === active ? "w-8 bg-gold" : "w-2 bg-ink/20 hover:bg-ink/40"}`} />))}</div>
      </div>
    </section>
  );
};

const Faq = () => { const { ui, FAQ } = useS(); const [open, setOpen] = useState(0); return (
  <section className="bg-ink text-white py-28 md:py-36 grain relative" data-testid="faq">
    <div className="max-w-4xl mx-auto px-6 md:px-12">
      <div className="text-center mb-16"><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.faqTitlePre}<span className="gold-text italic">{ui.faqTitleGold}</span></h2></div>
      <div className="divide-y divide-white/10 border-t border-white/10">
        {FAQ.map((f, i) => (
          <div key={i} className="py-6" data-testid={`faq-item-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-6 text-left group">
              <span className="font-serif text-xl md:text-2xl group-hover:text-gold transition-colors">{f.q}</span>
              <span className="shrink-0 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center group-hover:border-gold transition-colors">{open === i ? <Minus className="w-4 h-4 text-gold" /> : <Plus className="w-4 h-4" />}</span>
            </button>
            <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden"><p className="pt-5 text-white/55 font-light leading-relaxed max-w-2xl">{f.a}</p></motion.div>
          </div>
        ))}
      </div>
    </div>
  </section>
); };

const Contact = () => { const { ui, CONTACT } = useS(); return (
  <section id="contact" className="bg-white text-ink pt-28 md:pt-36" data-testid="contact">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
      <div>
        <Eyebrow>{ui.contactEye}</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{ui.contactPre}<span className="gold-text italic">{ui.contactGold}</span></h2>
        <Reveal delay={0.15}><p className="mt-8 text-ink/60 text-lg font-light leading-relaxed max-w-md">{ui.contactP}</p></Reveal>
        <div className="mt-10"><a href={CONTACT.phoneHref} data-testid="contact-call-btn"><GoldButton>{ui.contactCall} {CONTACT.phone} <Phone className="w-4 h-4" /></GoldButton></a></div>
        <div className="mt-12 space-y-6">
          <div className="flex gap-4"><MapPin className="w-5 h-5 text-gold shrink-0" /><p className="text-ink/70">{CONTACT.address}</p></div>
          <div className="flex gap-4"><Mail className="w-5 h-5 text-gold shrink-0" /><a href={`mailto:${CONTACT.email}`} className="text-ink/70 hover:text-gold transition-colors">{CONTACT.email}</a></div>
          <div className="flex gap-4"><Clock className="w-5 h-5 text-gold shrink-0" /><ul className="text-ink/70 space-y-1">{CONTACT.hours.map((h) => <li key={h.d} className="flex gap-4"><span className="w-40 text-ink/50">{h.d}</span><span>{h.t}</span></li>)}</ul></div>
        </div>
      </div>
      <Reveal delay={0.2}><div className="rounded-3xl overflow-hidden border border-ink/8 shadow-xl h-[520px]"><iframe title={ui.mapTitle} src={CONTACT.mapEmbed} className="w-full h-full grayscale" loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></div></Reveal>
    </div>
    <div className="h-28 md:h-36" />
  </section>
); };

export default function Home() {
  const { lang } = useLang();
  return (
    <>
      <Seo title={lang === "en" ? "Luxury Dental Care. Exceptional Smiles." : "Îngrijire Dentară de Lux. Zâmbete Excepționale."} description={lang === "en" ? "Glamour Dent — a five-star luxury dental studio: cosmetic dentistry, implants, veneers and orthodontics with concierge care." : "Glamour Dent — cabinet stomatologic de lux de cinci stele: estetică dentară, implanturi, fațete și ortodonție cu îngrijire concierge."} path="/" />
      <Hero /><StatsBar /><About /><Philosophy /><Marquee /><Why /><ServicesPreview /><Doctors /><Technology /><Journey /><Gallery /><Testimonials /><Faq /><Contact />
    </>
  );
}
